import type {
  ArtworkElementDependencyRule,
  ArtworkOverlayGroup,
  ArtworkOverlayOption
} from './digitalProofingArtworkTypes'
import type { DynamicArtworkField, DynamicFieldValue, DynamicFieldValues } from './dynamicProofingFormTypes'

type DependencyTargetType = ArtworkElementDependencyRule['target_type']
type DependencySourceType = ArtworkElementDependencyRule['conditions'][number]['source_type']

type ArtworkElementDependencyVisibilityInput = {
  artwork?:
    | {
        artwork_fields?: DynamicArtworkField[]
        artwork_overlay_groups?: ArtworkOverlayGroup[]
        dependency_rules?: ArtworkElementDependencyRule[]
      }
    | false
    | null
  fieldValues?: DynamicFieldValues
  selectedArtworkOverlayIds?: Array<number | string>
}

export type ArtworkElementDependencyVisibility = {
  visibleArtworkFieldIds: Set<string>
  visibleArtworkFieldOptionIds: Set<string>
  visibleArtworkOverlayGroupIds: Set<string>
  visibleArtworkOverlayIds: Set<string>
}

const dependencyKey = (type: DependencyTargetType | DependencySourceType, id: number | string | null | undefined) =>
  `${type}:${String(id)}`

const normalizeId = (id: number | string | null | undefined) => (id === null || id === undefined ? '' : String(id))

const fieldValueForDependency = (value: DynamicFieldValue): DynamicFieldValue => {
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return (value as { value: string }).value
  }

  return value
}

const hasDependencyValue = (value: DynamicFieldValue) => {
  const comparisonValue = fieldValueForDependency(value)
  return comparisonValue !== '' && comparisonValue !== '-1' && comparisonValue !== null && comparisonValue !== undefined
}

export const artworkOverlayDependencyId = (option?: ArtworkOverlayOption | null) =>
  normalizeId(option?.artwork_overlay_id ?? option?.id)

export const resolveArtworkElementDependencyVisibility = ({
  artwork,
  fieldValues = {},
  selectedArtworkOverlayIds = []
}: ArtworkElementDependencyVisibilityInput): ArtworkElementDependencyVisibility => {
  const fields = artwork && artwork.artwork_fields ? artwork.artwork_fields : []
  const groups = artwork && artwork.artwork_overlay_groups ? artwork.artwork_overlay_groups : []
  const rules = artwork && artwork.dependency_rules ? artwork.dependency_rules.filter(rule => rule.is_active) : []
  const fieldOptions = fields.flatMap(field => (field.artwork_field_options ?? []).map(option => ({ option, field })))
  const overlays = groups.flatMap(group => (group.artwork_overlays ?? []).map(option => ({ option, group })))

  const fieldsById = new Map(fields.map(field => [normalizeId(field.id), field]))
  const fieldOptionsById = new Map(fieldOptions.map(item => [normalizeId(item.option.id), item]))
  const groupsById = new Map(groups.map(group => [normalizeId(group.id), group]))
  const overlaysById = new Map(overlays.map(item => [artworkOverlayDependencyId(item.option), item]))
  const selectedOverlayIdSet = new Set(selectedArtworkOverlayIds.map(id => String(id)))
  const activeRulesByTarget = rules.reduce<Record<string, ArtworkElementDependencyRule[]>>((rulesByTarget, rule) => {
    const key = dependencyKey(rule.target_type, rule.target_id)
    rulesByTarget[key] = [...(rulesByTarget[key] ?? []), rule]
    return rulesByTarget
  }, {})
  const visibilityCache = new Map<string, boolean>()
  const visiting = new Set<string>()

  const submittedFieldValue = (field: DynamicArtworkField) =>
    fieldValueForDependency(fieldValues[field.name] ?? fieldValues[normalizeId(field.id)])

  const visible = (
    type: DependencyTargetType | DependencySourceType,
    id: number | string | null | undefined
  ): boolean => {
    const key = dependencyKey(type, id)
    if (visibilityCache.has(key)) return visibilityCache.get(key) ?? false
    if (visiting.has(key)) return false

    visiting.add(key)
    // Recursive dependency checks intentionally share the same cache and parent-visibility helpers.
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const isVisible = baseVisible(type, id) && dependencyVisible(type as DependencyTargetType, id)
    visibilityCache.set(key, isVisible)
    visiting.delete(key)
    return isVisible
  }

  const conditionMatches = (condition: ArtworkElementDependencyRule['conditions'][number]) => {
    if (condition.source_type === 'artwork_field') {
      const field = fieldsById.get(normalizeId(condition.source_id))
      if (!field || !visible('artwork_field', field.id)) return false

      const value = submittedFieldValue(field)
      switch (condition.operator) {
        case 'present':
          return hasDependencyValue(value)
        case 'blank':
          return !hasDependencyValue(value)
        case 'equals':
          return String(value ?? '') === String(condition.expected_value ?? '')
        case 'not_equals':
          return String(value ?? '') !== String(condition.expected_value ?? '')
        default:
          return false
      }
    }

    if (condition.source_type === 'artwork_field_option') {
      const item = fieldOptionsById.get(normalizeId(condition.source_id))
      if (!item || !visible('artwork_field_option', item.option.id)) return false

      const selected = String(submittedFieldValue(item.field) ?? '') === String(item.option.value)
      return condition.operator === 'selected' ? selected : !selected
    }

    if (condition.source_type === 'artwork_overlay') {
      const item = overlaysById.get(normalizeId(condition.source_id))
      if (!item || !visible('artwork_overlay', artworkOverlayDependencyId(item.option))) return false

      const selected = selectedOverlayIdSet.has(artworkOverlayDependencyId(item.option))
      return condition.operator === 'selected' ? selected : !selected
    }

    return false
  }

  const ruleMatches = (rule: ArtworkElementDependencyRule) => {
    if (rule.conditions.length === 0) return false

    const matches = rule.conditions.map(conditionMatches)
    return rule.match_mode === 'any' ? matches.some(Boolean) : matches.every(Boolean)
  }

  function baseVisible(type: DependencyTargetType | DependencySourceType, id: number | string | null | undefined) {
    if (type === 'artwork_field') {
      const field = fieldsById.get(normalizeId(id))
      return Boolean(field && field.is_active !== false && field.is_hidden !== true)
    }

    if (type === 'artwork_field_option') {
      const item = fieldOptionsById.get(normalizeId(id))
      return Boolean(item && item.option.is_active !== false && visible('artwork_field', item.field.id))
    }

    if (type === 'artwork_overlay_group') {
      const group = groupsById.get(normalizeId(id))
      return Boolean(group && (group as { is_active?: boolean }).is_active !== false)
    }

    if (type === 'artwork_overlay') {
      const item = overlaysById.get(normalizeId(id))
      return Boolean(
        item &&
        (item.option as { is_active?: boolean }).is_active !== false &&
        visible('artwork_overlay_group', item.group.id)
      )
    }

    return false
  }

  function dependencyVisible(type: DependencyTargetType, id: number | string | null | undefined) {
    const targetRules = activeRulesByTarget[dependencyKey(type, id)] ?? []
    if (targetRules.length === 0) return true

    // Store and backend both OR multiple rules for one target.
    return targetRules.some(ruleMatches)
  }

  return {
    visibleArtworkFieldIds: new Set(
      fields.filter(field => visible('artwork_field', field.id)).map(field => normalizeId(field.id))
    ),
    visibleArtworkFieldOptionIds: new Set(
      fieldOptions
        .filter(item => visible('artwork_field_option', item.option.id))
        .map(item => normalizeId(item.option.id))
    ),
    visibleArtworkOverlayGroupIds: new Set(
      groups.filter(group => visible('artwork_overlay_group', group.id)).map(group => normalizeId(group.id))
    ),
    visibleArtworkOverlayIds: new Set(
      overlays
        .filter(item => visible('artwork_overlay', artworkOverlayDependencyId(item.option)))
        .map(item => artworkOverlayDependencyId(item.option))
    )
  }
}

export const filterArtworkFieldsByDependencyVisibility = (
  fields: DynamicArtworkField[],
  visibility: ArtworkElementDependencyVisibility
) =>
  fields
    .map(field => ({
      ...field,
      artwork_field_options: (field.artwork_field_options ?? []).filter(option =>
        visibility.visibleArtworkFieldOptionIds.has(normalizeId(option.id))
      )
    }))
    .filter(field => visibility.visibleArtworkFieldIds.has(normalizeId(field.id)))

export const filterOverlayGroupsByDependencyVisibility = (
  groups: ArtworkOverlayGroup[],
  visibility: ArtworkElementDependencyVisibility
) =>
  groups
    .filter(group => visibility.visibleArtworkOverlayGroupIds.has(normalizeId(group.id)))
    .map(group => ({
      ...group,
      artwork_overlays: (group.artwork_overlays ?? []).filter(option =>
        visibility.visibleArtworkOverlayIds.has(artworkOverlayDependencyId(option))
      )
    }))
